import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCartContainer } from '@components/organisms/ShoppingCartContainer';
import { NewAddressForm } from '@components/molecules/NewAddressForm';
import { AddressOption } from '@components/molecules/AddressOption';
import { Card } from '@components/atoms/Card';
import { RadioButton, RadioButtonType } from '@components/atoms/RadioButton';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { SelectableAddress, ShoppingCartTotalData } from '@types';
import styles from './Billing.module.css';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useAddBillingAddressToCart } from '@services/useAddBillingAddressToCart';
import { useCustomNavigate } from '@hooks/useCustomNavigate';

const CURRENT_STEP = 2;

const ADDRESS_TYPE_OPTIONS = {
    common: {
        name: 'billing-address',
        type: RadioButtonType.normal,
    },
    existing: {
        id: 'existing-address-option',
        value: 'existing-address',
    },
    new: {
        id: 'new-address-option',
        value: 'new-address',
    },
};

export interface BillingProps {
    existingAddresses: SelectableAddress[];
    total: ShoppingCartTotalData;
}

export const Billing: FC<BillingProps> = ({ existingAddresses, total }) => {
    const { t } = useTranslation();
    const { addresses } = useData();
    const { request: requestAddBillingAddressToCart } = useAddBillingAddressToCart();

    const translations = {
        title: t('billing-component.title'),
        billingAddressTitle: t('billing-component.billing-address.title'),
        billingAddressSubtitle: t('billing-component.billing-address.subtitle'),
        useExistingAddress: t('billing-component.use-existing-address'),
        useNewAddress: t('billing-component.use-new-address'),
        continueButton: t('billing-component.continue.button'),
    };

    // choose either existing or new address
    const [addressType, setAddressType] = useState<string>(existingAddresses.length > 0 ? 'existing-address' : 'new-address');
    const handleOnChangeAddressType: (value: string) => void = (value) => setAddressType(value);

    // choose amongst existing addresses
    const [selectedExistingAddress, setSelectedExistingAddress] = useState<string | null>(
        existingAddresses.find((item) => item.initiallySelected)?.value || null
    );
    const handleOnChangeExistingAddress = (value: string) => setSelectedExistingAddress(value);
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);
    const [lastAddedAddress, setLastAddedAddress] = useState<SelectableAddress | null>(null);
    const { navigate } = useCustomNavigate();

    useEffect(() => {
        if (addressType === ADDRESS_TYPE_OPTIONS.existing.value && !lastAddedAddress) {
            setIsButtonEnabled(selectedExistingAddress !== null);
        } else {
            setIsButtonEnabled(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressType, selectedExistingAddress]);

    useEffect(() => {
        if (addressType === 'new-address') {
            setLastAddedAddress(existingAddresses[existingAddresses.length - 1]);
        }
        setAddressType('existing-address');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addresses]);

    const handleSaveAddress = (event: React.MouseEvent) => {
        event.preventDefault();

        let selectedUUID: string | undefined = undefined;

        if (addressType === ADDRESS_TYPE_OPTIONS.existing.value && selectedExistingAddress) {
            const selectedItem = existingAddresses.find((item) => item.value === selectedExistingAddress);
            selectedUUID = selectedItem ? selectedItem.uuid : undefined;
        }

        if (selectedUUID) {
            requestAddBillingAddressToCart({ address_uuid: selectedUUID });
            navigate(`/${SITE_URLS.orderDeliveryAddress}`);
        }
    };

    return (
        <ShoppingCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Anchor
                        href={SITE_URLS.orderDeliveryAddress}
                        onClick={handleSaveAddress}
                        mode={AnchorMode.primary}
                        disabled={!isButtonEnabled}>
                        {translations.continueButton}
                    </Anchor>
                </div>
            }
            total={total}>
            <Card classes={styles['billing-address-card']}>
                <div className={`${styles['card-header']}`}>
                    <i className="fa-thin fa-house"></i>
                    <div className="d-flex flex-column">
                        <Typography tagVariant={TypographyTagVariants.h2} classes="mb-1" fontSize="16" fontWeight="900">
                            {translations.billingAddressTitle}
                        </Typography>
                        <Typography classes="mb-0" fontSize="12" color="muted">
                            {translations.billingAddressSubtitle}
                        </Typography>
                    </div>
                </div>
                <div className={`card-body ${styles['card-body']}`}>
                    {existingAddresses.length > 0 && (
                        <>
                            <div className={styles.indent}>
                                <div className="d-flex align-items-center mb-2 pb-1">
                                    <div className={styles['radio-container']}>
                                        <RadioButton
                                            {...ADDRESS_TYPE_OPTIONS.common}
                                            {...ADDRESS_TYPE_OPTIONS.existing}
                                            checked={ADDRESS_TYPE_OPTIONS.existing.value === addressType}
                                            onChange={handleOnChangeAddressType}
                                        />
                                    </div>
                                    <Typography fontSize="14">{translations.useExistingAddress}</Typography>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div className={styles['radio-container']}>
                                        <RadioButton
                                            {...ADDRESS_TYPE_OPTIONS.common}
                                            {...ADDRESS_TYPE_OPTIONS.new}
                                            checked={ADDRESS_TYPE_OPTIONS.new.value === addressType}
                                            onChange={handleOnChangeAddressType}
                                        />
                                    </div>
                                    <Typography fontSize="14">{translations.useNewAddress}</Typography>
                                </div>
                            </div>
                            <hr className="my-0" />
                        </>
                    )}
                    {addressType === ADDRESS_TYPE_OPTIONS.existing.value && existingAddresses.length > 0 ? (
                        <div id="existing-address-selector" className={`${styles.indent} row g-4 pe-2`}>
                            {existingAddresses.map((address, index) => (
                                <div key={`existing-address-${index}`} className="col-12 col-xl-6">
                                    <AddressOption
                                        radioButton={{
                                            id: `existing-address-${index}`,
                                            name: 'existing-address',
                                            value: address.value,
                                            type: RadioButtonType.greenVariant,
                                            checked:
                                                address.value === selectedExistingAddress ||
                                                lastAddedAddress?.value === address.value,
                                            onChange: handleOnChangeExistingAddress,
                                        }}
                                        name={address.name}
                                        lastName={address.lastName}
                                        fiscalNumber={address.fiscalNumber}
                                        address={address.address}
                                        postalCode={address.postalCode}
                                        region={address.region}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <NewAddressForm type={'billing'} />
                    )}
                </div>
            </Card>
        </ShoppingCartContainer>
    );
};
